import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, parseISO, addDays, isBefore, isAfter } from "date-fns";

export interface Alert {
  id: string;
  type: "medical" | "exam" | "subject" | "appointment";
  priority: "urgent" | "warning" | "info";
  title: string;
  message: string;
  studentId: string;
  studentName: string;
  daysRemaining?: number;
  link: string;
}

const fetchAlerts = async (): Promise<Alert[]> => {
  const alerts: Alert[] = [];
  const today = new Date();

  // Fetch students with their subjects
  const { data: students, error: studentsError } = await supabase
    .from("students")
    .select(`
      id, first_name, last_name, calling_name,
      class_1_medical, class_2_medical,
      class1_appointment_date, class2_appointment_date,
      class1_appointment_scheduled, class2_appointment_scheduled
    `);

  if (studentsError) throw studentsError;

  // Fetch student subjects for exam alerts
  const { data: studentSubjects, error: subjectsError } = await supabase
    .from("student_subjects")
    .select(`
      id, student_id, subject_id, status, start_date,
      exam_result, exam_fail_date,
      subjects(name)
    `);

  if (subjectsError) throw subjectsError;

  // Process each student
  for (const student of students || []) {
    const studentName = student.calling_name || `${student.first_name} ${student.last_name || ''}`.trim();
    const studentLink = `/students/${student.id}`;

    // Medical Alerts - Class 2 (must be done first)
    if (student.class_2_medical === "Pending") {
      alerts.push({
        id: `medical-2-${student.id}`,
        type: "medical",
        priority: "warning",
        title: "Class 2 Medical Pending",
        message: `${studentName} has pending Class 2 medical`,
        studentId: student.id,
        studentName,
        link: studentLink,
      });
    }

    // Medical Alerts - Class 1 (only show if Class 2 is done)
    if (student.class_1_medical === "Pending" && student.class_2_medical === "Done") {
      alerts.push({
        id: `medical-1-${student.id}`,
        type: "medical",
        priority: "warning",
        title: "Class 1 Medical Pending",
        message: `${studentName} has pending Class 1 medical (Class 2 done)`,
        studentId: student.id,
        studentName,
        link: studentLink,
      });
    }

    // Upcoming Class 2 Appointment (within 7 days) - shown first as it must be done before Class 1
    if (student.class2_appointment_scheduled && student.class2_appointment_date) {
      const appointmentDate = parseISO(student.class2_appointment_date);
      const daysUntil = differenceInDays(appointmentDate, today);
      
      if (daysUntil >= 0 && daysUntil <= 7) {
        alerts.push({
          id: `appt-2-${student.id}`,
          type: "appointment",
          priority: daysUntil <= 2 ? "urgent" : "info",
          title: "Upcoming Class 2 Appointment",
          message: `${studentName}'s appointment in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
          studentId: student.id,
          studentName,
          daysRemaining: daysUntil,
          link: studentLink,
        });
      } else if (daysUntil < 0 && student.class_2_medical !== "Done") {
        alerts.push({
          id: `appt-overdue-2-${student.id}`,
          type: "appointment",
          priority: "urgent",
          title: "Overdue Class 2 Appointment",
          message: `${studentName}'s appointment was ${Math.abs(daysUntil)} days ago`,
          studentId: student.id,
          studentName,
          link: studentLink,
        });
      }
    }

    // Upcoming Class 1 Appointment (within 7 days) - only show if Class 2 is done
    if (student.class1_appointment_scheduled && student.class1_appointment_date && student.class_2_medical === "Done") {
      const appointmentDate = parseISO(student.class1_appointment_date);
      const daysUntil = differenceInDays(appointmentDate, today);
      
      if (daysUntil >= 0 && daysUntil <= 7) {
        alerts.push({
          id: `appt-1-${student.id}`,
          type: "appointment",
          priority: daysUntil <= 2 ? "urgent" : "info",
          title: "Upcoming Class 1 Appointment",
          message: `${studentName}'s appointment in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
          studentId: student.id,
          studentName,
          daysRemaining: daysUntil,
          link: studentLink,
        });
      } else if (daysUntil < 0 && student.class_1_medical !== "Done") {
        // Overdue appointment
        alerts.push({
          id: `appt-overdue-1-${student.id}`,
          type: "appointment",
          priority: "urgent",
          title: "Overdue Class 1 Appointment",
          message: `${studentName}'s appointment was ${Math.abs(daysUntil)} days ago`,
          studentId: student.id,
          studentName,
          link: studentLink,
        });
      }
    }
    if (student.class2_appointment_scheduled && student.class2_appointment_date) {
      const appointmentDate = parseISO(student.class2_appointment_date);
      const daysUntil = differenceInDays(appointmentDate, today);
      
      if (daysUntil >= 0 && daysUntil <= 7) {
        alerts.push({
          id: `appt-2-${student.id}`,
          type: "appointment",
          priority: daysUntil <= 2 ? "urgent" : "info",
          title: "Upcoming Class 2 Appointment",
          message: `${studentName}'s appointment in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
          studentId: student.id,
          studentName,
          daysRemaining: daysUntil,
          link: studentLink,
        });
      } else if (daysUntil < 0 && student.class_2_medical !== "Done") {
        alerts.push({
          id: `appt-overdue-2-${student.id}`,
          type: "appointment",
          priority: "urgent",
          title: "Overdue Class 2 Appointment",
          message: `${studentName}'s appointment was ${Math.abs(daysUntil)} days ago`,
          studentId: student.id,
          studentName,
          link: studentLink,
        });
      }
    }
  }

  // Process student subjects for exam and progress alerts
  for (const ss of studentSubjects || []) {
    const student = students?.find(s => s.id === ss.student_id);
    if (!student) continue;

    const studentName = student.calling_name || `${student.first_name} ${student.last_name || ''}`.trim();
    const studentLink = `/students/${student.id}`;
    const subjectName = (ss.subjects as any)?.name || 'Unknown Subject';

    // Exam Reappear Alert (45-day countdown for failed exams)
    if (ss.exam_result === "Fail" && ss.exam_fail_date) {
      const failDate = parseISO(ss.exam_fail_date);
      const reappearDate = addDays(failDate, 45);
      const daysUntilReappear = differenceInDays(reappearDate, today);

      if (daysUntilReappear > 0) {
        alerts.push({
          id: `exam-reappear-${ss.id}`,
          type: "exam",
          priority: daysUntilReappear <= 7 ? "info" : "warning",
          title: "Exam Reappear Pending",
          message: `${studentName} can reappear for ${subjectName} in ${daysUntilReappear} days`,
          studentId: student.id,
          studentName,
          daysRemaining: daysUntilReappear,
          link: studentLink,
        });
      } else {
        // Can now reappear
        alerts.push({
          id: `exam-eligible-${ss.id}`,
          type: "exam",
          priority: "urgent",
          title: "Eligible for Exam Reappear",
          message: `${studentName} can now reappear for ${subjectName} exam`,
          studentId: student.id,
          studentName,
          link: studentLink,
        });
      }
    }

    // Long-pending subjects (started > 60 days ago, not completed)
    if (ss.status === "In Progress" && ss.start_date) {
      const startDate = parseISO(ss.start_date);
      const daysSinceStart = differenceInDays(today, startDate);

      if (daysSinceStart > 60) {
        alerts.push({
          id: `subject-pending-${ss.id}`,
          type: "subject",
          priority: daysSinceStart > 90 ? "warning" : "info",
          title: "Long-Pending Subject",
          message: `${studentName}'s ${subjectName} has been in progress for ${daysSinceStart} days`,
          studentId: student.id,
          studentName,
          link: studentLink,
        });
      }
    }
  }

  // Sort by priority
  const priorityOrder = { urgent: 0, warning: 1, info: 2 };
  return alerts.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
};

export const useAlerts = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["alerts"],
    queryFn: fetchAlerts,
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
